/** 貸与品台帳。台帳が動くのはタスクの完了と備品ページの即時貸出/返却だけ(表示専用)。 */
import { Alert, Card, Flex, Radio, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { Loan } from "../../lib/api";
import { jst } from "../../lib/format";
import { errorText } from "./hooks";
import { useLoans } from "./loanHooks";

/** 台帳の行に出す「どの申請で貸して、どの申請で返したか」。 */
function requestText(row: Loan) {
  const lent = row.request_id ? `#${row.request_id}` : "—";
  return (
    <span>
      {row.request_id ? <Link to={`/hr/wf-requests/${row.request_id}`}>{lent}</Link> : lent}
      {row.return_request_id ? (
        <>
          {" → "}
          <Link to={`/hr/wf-requests/${row.return_request_id}`}>#{row.return_request_id}</Link>
        </>
      ) : null}
    </span>
  );
}

export function LoansPage() {
  const [openOnly, setOpenOnly] = useState(true);
  const query = useLoans({ open: openOnly });

  const columns: ColumnsType<Loan> = [
    {
      title: "対象者",
      width: 280,
      render: (_: unknown, row) => (
        <span>
          <Link to={`/hr/employees/${row.employee_id}`}>{row.employee_name}</Link>{" "}
          <Typography.Text type="secondary">{row.emp_code}</Typography.Text>
        </span>
      ),
    },
    { title: "品目", dataIndex: "item", width: 160 },
    {
      title: "管理番号",
      width: 320,
      render: (_: unknown, row) =>
        row.asset_no ? (
          <Link to={`/hr/equipment?q=${encodeURIComponent(row.asset_no)}`}>
            {row.asset_no}
            {row.equipment_name ? (
              <Typography.Text type="secondary"> {row.equipment_name}</Typography.Text>
            ) : null}
          </Link>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: "セクション",
      dataIndex: "section",
      width: 160,
      render: (v: string) => v || "—",
    },
    { title: "貸与日", width: 180, render: (_: unknown, row) => jst(row.lent_at) },
    {
      title: "返却日",
      width: 180,
      render: (_: unknown, row) =>
        row.returned_at ? jst(row.returned_at) : <Tag color="gold">貸与中</Tag>,
    },
    { title: "申請", width: 180, render: (_: unknown, row) => requestText(row) },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          貸与品台帳
        </Typography.Title>
        <Radio.Group
          value={openOnly ? "open" : "all"}
          onChange={(e) => setOpenOnly(e.target.value === "open")}
          optionType="button"
          options={[
            { value: "open", label: "貸与中のみ" },
            { value: "all", label: "返却済みも表示" },
          ]}
        />
      </Flex>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="貸与品台帳を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<Loan>
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={query.data?.items ?? []}
          loading={query.isFetching}
          scroll={{ x: 1460 }}
          pagination={{ pageSize: 50, showTotal: (t) => `全 ${t} 件` }}
          locale={{ emptyText: openOnly ? "貸与中の貸与品なし" : "貸与品なし" }}
        />
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
          台帳が動くのは<strong>タスクの完了</strong>(貸与品の工程を完了 → 貸与 / 返却の工程を完了
          → 返却)と、備品マスタからの即時貸出・返却だけ。工程の貸与品フラグは
          <Link to="/hr/workflow">WF設計</Link>のテンプレート編集で設定する。
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
